import { WPN_POPUP_STATUS } from '../../../../global-store/subjects/auto-push-notifications/get-actions';
import { commonActions } from '../../../../global-store/subjects/common';
import { getLabel } from '../../../../labels/labelling-service';
import logger from '../../../../services/logging/logging-service';
import { satelliteTracker } from '../../../../services/tracking/satellite-tracking-service';
import { WPN_ANALYTICS_EVENTS } from '../../../../utils/analytics';
import { RESPONSIVE_OFFSETS } from '../../../../utils/constants';
import { closeBurgerMenu, scrollToElement } from '../../../../utils/dom-utils';
import {
  setDailySessionOnLocalStorage,
  setStatusOnLocalStorage,
  WPN_DAILY_SESSION,
  WPN_STATUS,
} from '../../../../utils/local-storage';
import {
  optOut,
  setOptionsFromSdk,
  setOptionsToSdk,
  getAnalyticsTopics,
  getAnalyticsCategories,
  showPreferencesDialog,
  initDefaultOptions,
} from '../../../../utils/push-notifications-utils';

const getHandleShowConfirmModal = (actions) => () => {
  actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_CONFIRM_MODAL);
  commonActions.setPushNotificationInDialogShow(true);
  satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.OPEN);
  setStatusOnLocalStorage({
    key: WPN_STATUS.OBJECT_KEY,
    name: WPN_STATUS.KO_SOFT.NAME,
    expirationDays: WPN_STATUS.KO_SOFT.EXPIRATION_DAYS,
  });
  setDailySessionOnLocalStorage(WPN_DAILY_SESSION.OBJECT_KEY, WPN_DAILY_SESSION.EXPIRATION_DAYS);
};

const getHandleConfirmModalInDialogAccept = (actions) => async (selectBoxes) => {
  if (Notification?.permission !== 'granted' && Notification.requestPermission) {
    try {
      const state = await Notification.requestPermission();
      if (state) {
        showPreferencesDialog({ notificationPermission: state, selectBoxes, actions });
      }
    } catch (error) {
      logger.error(`[WPN] There has been a problem with request permission: ${error.message}`);
    }
  } else {
    showPreferencesDialog({ notificationPermission: 'granted', selectBoxes, actions });
  }

  setStatusOnLocalStorage({
    key: WPN_STATUS.OBJECT_KEY,
    name: WPN_STATUS.OK.NAME,
    expirationDays: WPN_STATUS.OK.EXPIRATION_DAYS,
  });

  satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.JOIN);
};

const getHandleConfirmModalCancel = (actions) => (selectBoxes, allUnchecked) => {
  setStatusOnLocalStorage({
    key: WPN_STATUS.OBJECT_KEY,
    name: WPN_STATUS.KO.NAME,
    expirationDays: WPN_STATUS.KO.EXPIRATION_DAYS,
  });

  initDefaultOptions({ selectBoxes, actions, allUnchecked });

  actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
  logger.error('[WPN] Not subscribed - NOT NOW --> KO');
};

const getHandleConfirmModalClose = (actions) => (selectBoxes, allUnchecked) => {
  setStatusOnLocalStorage({
    key: WPN_STATUS.OBJECT_KEY,
    name: WPN_STATUS.KO_SOFT.NAME,
    expirationDays: WPN_STATUS.KO_SOFT.EXPIRATION_DAYS,
  });

  initDefaultOptions({ selectBoxes, actions, allUnchecked });
  actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
  logger.error('[WPN] Not subscribed - CLOSED --> KO_SOFT');
};

const getHandleWPNunsubscribe = (actions) => () => {
  actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_PREFERENCES_UNSUBSCRIBE_CONFIRM);
};

const getHandleWPNunsubscribeConfirm = (sdk, actions) => async () => {
  try {
    await optOut(sdk);
    actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_PREFERENCES_UNSUB_SUCCESS);
    setTimeout(() => {
      actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
    }, 3000);
  } catch (error) {
    logger.error(`[WPN] UA - error trying to optOut: ${error}`);
  }
};

const getHandlePreferencesDialogClose = (sdk, actions) => (selectBoxes) => {
  setOptionsFromSdk({ sdk, selectBoxes, actions });
  actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
};

const getHandleGenericDialogClose = (actions) => () => {
  actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
};

const getHandlePreferencesDialogConfirm = (sdk, actions) => async (selectBoxes, selectedOptions) => {
  try {
    await setOptionsToSdk({ sdk, selectBoxes, selectedOptions });
    actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_CONFIRM_MODAL_SUCCESS);
    setTimeout(() => {
      actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
    }, 3000);

    const categoriesAndTopics = {
      category: getAnalyticsCategories(selectBoxes, selectedOptions),
      name: getAnalyticsTopics(selectBoxes, selectedOptions),
    };
    satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.EDIT, categoriesAndTopics);
  } catch (error) {
    logger.error(error);
  }
};

const getHandleWPNEditClick = (sdk, actions) => (selectBoxes) => {
  satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.ICONA);

  closeBurgerMenu();

  if (!sdk?.channel?.optedIn) {
    initDefaultOptions({ selectBoxes, actions });
  }
  if (Notification?.permission === 'granted') {
    actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_PREFERENCES_POPUP);
  } else {
    actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_CONFIRM_MODAL);
    satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.OPEN);
  }
};

const getHandleOptionClick = (state, actions) => (selectBoxName, option) => {
  const currentSelectedOptions = state.selectedOptions[selectBoxName];
  let newSelectedOptions;
  if (currentSelectedOptions?.length) {
    newSelectedOptions = currentSelectedOptions.includes(option.value)
      ? currentSelectedOptions.filter((selectedOption) => selectedOption !== option.value)
      : [...currentSelectedOptions, option.value];
  } else {
    newSelectedOptions = [option.value];
  }
  actions.setSelectedOptions(selectBoxName, newSelectedOptions);
};

const getHandleSwitchClick = (actions) => (selectBox, checked) => {
  const newSelectedOptions = checked ? [] : selectBox.options.map((option) => option.value);
  actions.setSelectedOptions(selectBox.boxName, newSelectedOptions);
};

// IN PAGE
const getHandleConfirmModalInPageAccept = (actions, pageActions) => async (selectBoxes, blockType) => {
  if (Notification?.permission !== 'granted' && Notification.requestPermission) {
    try {
      const state = await Notification.requestPermission();
      if (state === 'granted') {
        pageActions.setActiveBlock(blockType);
        pageActions.setConfirmBottomDialog({ isShown: true });
      } else {
        initDefaultOptions({ selectBoxes, actions, allUnchecked: true });
      }
      actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
    } catch (error) {
      logger.error(`[WPN] There has been a problem with request permission: ${error.message}`);
    }
  } else {
    actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
  }

  setStatusOnLocalStorage({
    key: WPN_STATUS.OBJECT_KEY,
    name: WPN_STATUS.OK.NAME,
    expirationDays: WPN_STATUS.OK.EXPIRATION_DAYS,
  });

  satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.JOIN);
};

const getHandleWPNEditInPageClick = (pageActions) => (elementIdInPage) => {
  satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.ICONA);
  closeBurgerMenu();
  pageActions.selectTab(2);
  const responsiveOffset = RESPONSIVE_OFFSETS.default;
  const $element = document.getElementById(elementIdInPage);
  scrollToElement({ $element, top: true, responsiveOffset, delay: 250 });
};

const getHandleOptionInPageClick =
  ({ actions, pageActions, state }) =>
  ({ selectBoxName, option, blockType }) => {
    const handleOptionClick = getHandleOptionClick(state, actions);
    handleOptionClick(selectBoxName, option);
    if (Notification?.permission === 'granted') {
      pageActions.setActiveBlock(blockType);
      pageActions.setConfirmBottomDialog({ isShown: true });
    } else {
      actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_CONFIRM_MODAL);
      satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.OPEN);
    }
  };

const getHandleSwitchInPageClick =
  (actions, pageActions) =>
  ({ selectBox, checked, blockType }) => {
    const handleSwitchClick = getHandleSwitchClick(actions);
    handleSwitchClick(selectBox, checked);
    if (Notification?.permission === 'granted') {
      pageActions.setActiveBlock(blockType);
      pageActions.setConfirmBottomDialog({ isShown: true });
    } else {
      actions.setPopupStatus(WPN_POPUP_STATUS.SHOW_CONFIRM_MODAL);
      satelliteTracker.trackInWeb(WPN_ANALYTICS_EVENTS.OPEN);
    }
  };

const getHandleWPNunsubscribeConfirmInPage =
  ({ sdk, actions, pageActions }) =>
  async (selectBoxes) => {
    try {
      await optOut(sdk);
      actions.setPopupStatus(WPN_POPUP_STATUS.NOTHING_IS_SHOWN);
      pageActions.setNotificationStatus({ isShown: true, message: getLabel('status.notification.message.remove.WPN') });
      pageActions.setNotificationStatus({ isShown: false }, 3000);
      initDefaultOptions({ selectBoxes, actions, allUnchecked: true });
    } catch (error) {
      logger.error(`[WPN] UA - error trying to optOut: ${error}`);
    }
  };

export const getHandlers = ({ state, actions, pageActions }) => ({
  handleShowConfirmModal: getHandleShowConfirmModal(actions),
  handleConfirmModalInDialogAccept: getHandleConfirmModalInDialogAccept(actions),
  handleConfirmModalCancel: getHandleConfirmModalCancel(actions),
  handleConfirmModalClose: getHandleConfirmModalClose(actions),
  handleWPNunsubscribe: getHandleWPNunsubscribe(actions),
  handleWPNunsubscribeConfirm: getHandleWPNunsubscribeConfirm(state.sdk, actions),
  handleWPNunsubscribeConfirmInPage: getHandleWPNunsubscribeConfirmInPage({ sdk: state.sdk, actions, pageActions }),
  handlePreferencesDialogClose: getHandlePreferencesDialogClose(state.sdk, actions),
  handlePreferencesDialogConfirm: getHandlePreferencesDialogConfirm(state.sdk, actions),
  handleGenericDialogClose: getHandleGenericDialogClose(actions),
  handleWPNEditClick: getHandleWPNEditClick(state.sdk, actions),
  handleOptionClick: getHandleOptionClick(state, actions),
  handleOptionInPageClick: getHandleOptionInPageClick({ actions, pageActions, state }),
  handleWPNEditInPageClick: getHandleWPNEditInPageClick(pageActions),
  handleConfirmModalInPageAccept: getHandleConfirmModalInPageAccept(actions, pageActions),
  handleSwitchInPageClick: getHandleSwitchInPageClick(actions, pageActions),
  handleSwitchClick: getHandleSwitchClick(actions),
});
